import './customer.css';

import type { CustomerPriceSearchResult, PriceCheckerQuote } from '@pos/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  customerApi,
  type CustomerConfig,
  type CustomerContext,
  CustomerError,
} from './api';

type Liff = (typeof import('@line/liff'))['default'];
type Screen = 'loading' | 'login' | 'friend' | 'ready' | 'error' | 'setup';
const baht = (value: number) =>
  (value / 100).toLocaleString('th-TH', { minimumFractionDigits: 2 });

function Camera({
  onRead,
  onClose,
}: {
  onRead: (barcode: string) => void;
  onClose: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let disposed = false;
    let controls: { stop(): void } | undefined;
    let stream: MediaStream | undefined;
    let delivered = false;
    const stop = () => {
      controls?.stop();
      stream?.getTracks().forEach((track) => track.stop());
    };
    void (async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (disposed) return;
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: 'environment' } },
        });
        if (disposed) {
          stop();
          return;
        }
        controls = await new BrowserMultiFormatReader().decodeFromStream(
          stream,
          video.current!,
          (result, _error, activeControls) => {
            if (result && !disposed && !delivered) {
              delivered = true;
              activeControls.stop();
              stop();
              onRead(result.getText());
            }
          },
        );
        if (disposed || delivered) stop();
      } catch {
        stop();
        if (!disposed)
          setError(
            'เปิดกล้องไม่ได้ กรุณาอนุญาตกล้อง หรือกลับไปพิมพ์เลขบาร์โค้ด',
          );
      }
    })();
    const hidden = () => {
      if (document.hidden) {
        disposed = true;
        stop();
        onClose();
      }
    };
    document.addEventListener('visibilitychange', hidden);
    return () => {
      disposed = true;
      stop();
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [onRead, onClose]);
  return (
    <section className="customer-camera" aria-label="กล้องสแกนบาร์โค้ด">
      <video ref={video} autoPlay muted playsInline />
      <p role={error ? 'alert' : undefined}>
        {error || 'เล็งกล้องให้เห็นบาร์โค้ดครบทั้งแถบ'}
      </p>
      <button type="button" onClick={onClose}>
        ปิดกล้อง / พิมพ์เลขเอง
      </button>
    </section>
  );
}

export function CustomerPriceView() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'แป้งสวย | เช็กราคาสินค้า';
    return () => {
      document.title = previousTitle;
    };
  }, []);
  const [screen, setScreen] = useState<Screen>('loading');
  const [config, setConfig] = useState<CustomerConfig>();
  const [context, setContext] = useState<CustomerContext>();
  const [branch, setBranch] = useState('');
  const [barcode, setBarcode] = useState('');
  const [query, setQuery] = useState('');
  const [searchResult, setSearchResult] = useState<CustomerPriceSearchResult>();
  const [quote, setQuote] = useState<PriceCheckerQuote>();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const sdk = useRef<Liff>();
  const request = useRef<AbortController>();
  const sequence = useRef(0);
  const bootSequence = useRef(0);

  const boot = useCallback(async () => {
    const current = ++bootSequence.current;
    setScreen('loading');
    setSearchResult(undefined);
    setQuote(undefined);
    setMessage('');
    try {
      const next = await customerApi.config();
      if (current !== bootSequence.current) return;
      setConfig(next);
      if (!next.liffId) {
        setScreen('setup');
        return;
      }
      sdk.current = (await import('@line/liff')).default;
      await sdk.current.init({ liffId: next.liffId });
      if (current !== bootSequence.current) return;
      if (!sdk.current.isLoggedIn()) {
        setScreen('login');
        return;
      }
      const token = sdk.current.getAccessToken();
      if (!token) {
        setScreen('login');
        return;
      }
      const result = await customerApi.context(token);
      if (current !== bootSequence.current) return;
      setContext(result);
      let previous: string | null = null;
      try {
        previous = localStorage.getItem(`customer-branch:${next.liffId}`);
      } catch {
        /* Storage may be unavailable in private browsing. */
      }
      setBranch(
        result.branches.find((item) => item.id === previous)?.id ??
          (result.branches.length === 1 ? result.branches[0].id : ''),
      );
      setScreen('ready');
    } catch (error) {
      if (current !== bootSequence.current) return;
      if (error instanceof CustomerError && error.reason === 'friend_required')
        setScreen('friend');
      else if (error instanceof CustomerError && error.status === 401)
        setScreen('login');
      else {
        setScreen('error');
        setMessage('ยังเชื่อมต่อบริการไม่ได้ กรุณาลองใหม่อีกครั้ง');
      }
    }
  }, []);
  useEffect(() => {
    void boot();
    return () => {
      ++bootSequence.current;
      ++sequence.current;
      request.current?.abort();
    };
  }, [boot]);

  const closeCamera = useCallback(() => setCamera(false), []);
  const lookup = useCallback(
    async (value: string) => {
      setCamera(false);
      setSearchResult(undefined);
      setQuote(undefined);
      setMessage('');
      if (!value.trim() || !branch) return;
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      const current = ++sequence.current;
      setBarcode(value);
      setBusy(true);
      try {
        const token = sdk.current?.getAccessToken();
        if (!token) {
          setScreen('login');
          return;
        }
        const result = await customerApi.lookup(
          token,
          branch,
          value.trim(),
          controller.signal,
        );
        if (current === sequence.current) setQuote(result);
      } catch (error) {
        if (current !== sequence.current) return;
        if (error instanceof CustomerError && error.status === 401)
          setScreen('login');
        else if (
          error instanceof CustomerError &&
          error.reason === 'friend_required'
        )
          setScreen('friend');
        else
          setMessage(
            error instanceof CustomerError && error.status === 404
              ? 'ไม่พบสินค้านี้ กรุณาตรวจเลขบาร์โค้ดหรือสอบถามพนักงาน'
              : error instanceof CustomerError && error.status === 429
                ? 'เช็กราคาถี่เกินไป กรุณารอสักครู่แล้วลองใหม่'
                : 'เช็กราคาไม่สำเร็จ กรุณาตรวจอินเทอร์เน็ตแล้วลองใหม่',
          );
      } finally {
        if (current === sequence.current) setBusy(false);
      }
    },
    [branch],
  );
  const search = async () => {
    if (!branch || query.trim().length < 2) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const current = ++sequence.current;
    setBusy(true);
    setCamera(false);
    setQuote(undefined);
    setSearchResult(undefined);
    setMessage('');
    try {
      const token = sdk.current?.getAccessToken();
      if (!token) {
        setScreen('login');
        return;
      }
      const result = await customerApi.search(
        token,
        branch,
        query.trim(),
        controller.signal,
      );
      if (current === sequence.current) setSearchResult(result);
    } catch (error) {
      if (current !== sequence.current) return;
      if (error instanceof CustomerError && error.status === 401)
        setScreen('login');
      else if (
        error instanceof CustomerError &&
        error.reason === 'friend_required'
      )
        setScreen('friend');
      else
        setMessage(
          error instanceof CustomerError && error.status === 429
            ? 'ค้นหาถี่เกินไป กรุณารอสักครู่แล้วลองใหม่'
            : 'ค้นหาไม่สำเร็จ กรุณาตรวจอินเทอร์เน็ตแล้วลองใหม่',
        );
    } finally {
      if (current === sequence.current) setBusy(false);
    }
  };
  const onRead = useCallback(
    (value: string) => {
      void lookup(value);
    },
    [lookup],
  );
  const login = () => {
    if (!sdk.current) {
      void boot();
      return;
    }
    if (sdk.current.isLoggedIn()) sdk.current.logout();
    sdk.current.login({
      redirectUri: `${location.origin}/customer/price-checker`,
    });
  };
  const addFriend = async () => {
    try {
      if (sdk.current?.isApiAvailable('requestFriendship')) {
        await sdk.current.requestFriendship();
        await boot();
        return;
      }
    } catch {
      /* An OA link remains available on older LINE clients. */
    }
    if (config?.addFriendUrl) location.assign(config.addFriendUrl);
  };

  return (
    <main className="customer-page">
      <header className="customer-header">
        <div className="customer-brand-lockup">
          <img
            className="customer-logo"
            src="/pangsuay_logo.png"
            alt="โลโก้ร้านแป้งสวย"
          />
          <div>
            <span className="customer-brand">ร้านแป้งสวย</span>
            <span className="customer-brand-caption">เช็กราคาด้วยตัวเอง</span>
          </div>
        </div>
        <span className="customer-header-label">เช็กราคาสินค้า</span>
      </header>
      <div className="customer-content">
        <p className="customer-eyebrow">รู้ราคาก่อนเลือกซื้อ</p>
        <h1>หยิบ สแกน เช็กราคา</h1>
        <p className="customer-intro">
          ราคาสินค้าและโปรโมชั่นของสาขาที่คุณเลือก
        </p>
        {screen === 'loading' && (
          <p role="status">กำลังตรวจสอบการเข้าใช้งาน…</p>
        )}
        {screen === 'setup' && (
          <section className="customer-card">
            <h2>กำลังเตรียมบริการ</h2>
            <p>ร้านกำลังเชื่อมต่อ LINE กรุณากลับมาใช้งานอีกครั้งภายหลัง</p>
          </section>
        )}
        {screen === 'login' && (
          <section className="customer-card">
            <h2>เริ่มต้นด้วยบัญชี LINE</h2>
            <p>เข้าใช้งานและเพิ่มเพื่อนกับร้าน เพื่อสแกนเช็กราคาได้ทันที</p>
            <button className="customer-primary" onClick={login}>
              เข้าใช้งานด้วย LINE
            </button>
          </section>
        )}
        {screen === 'friend' && (
          <section className="customer-card">
            <h2>เพิ่มเพื่อนกับร้านก่อนใช้งาน</h2>
            <p>
              เพิ่มเพื่อนหรือปลดบล็อก LINE OA ของร้านแป้งสวย
              แล้วกลับมาตรวจสอบอีกครั้ง
            </p>
            <button
              className="customer-primary"
              onClick={() => void addFriend()}
            >
              เพิ่มเพื่อน / ปลดบล็อก
            </button>
            <button onClick={() => void boot()}>
              เพิ่มเพื่อนแล้ว ตรวจสอบอีกครั้ง
            </button>
          </section>
        )}
        {screen === 'error' && (
          <section className="customer-card">
            <p role="alert">{message}</p>
            <button onClick={() => void boot()}>ลองใหม่</button>
          </section>
        )}
        {screen === 'ready' && (
          <>
            <label className="customer-branch">
              สาขาที่คุณกำลังซื้อ
              <select
                value={branch}
                onChange={(event) => {
                  ++sequence.current;
                  request.current?.abort();
                  setBusy(false);
                  setCamera(false);
                  setQuote(undefined);
                  setSearchResult(undefined);
                  setMessage('');
                  setBranch(event.target.value);
                  try {
                    localStorage.setItem(
                      `customer-branch:${config?.liffId}`,
                      event.target.value,
                    );
                  } catch {
                    /* Optional preference. */
                  }
                }}
              >
                <option value="">เลือกสาขา</option>
                {context?.branches.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            {camera ? (
              <Camera onRead={onRead} onClose={closeCamera} />
            ) : (
              <section className="customer-card">
                <button
                  className="customer-primary customer-scan"
                  disabled={!branch || busy}
                  onClick={() => {
                    setQuote(undefined);
                    setSearchResult(undefined);
                    setMessage('');
                    setCamera(true);
                  }}
                >
                  <svg
                    className="customer-scan-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M7 8v8m3-8v8m4-8v8m3-8v8" />
                  </svg>
                  เปิดกล้องสแกนบาร์โค้ด
                </button>
                <p className="customer-or">หรือพิมพ์เลขใต้บาร์โค้ด</p>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void lookup(barcode);
                  }}
                >
                  <label htmlFor="customer-barcode">เลขบาร์โค้ด</label>
                  <div className="customer-input-row">
                    <input
                      id="customer-barcode"
                      value={barcode}
                      maxLength={200}
                      autoComplete="off"
                      onChange={(event) => {
                        ++sequence.current;
                        request.current?.abort();
                        setBusy(false);
                        setSearchResult(undefined);
                        setQuote(undefined);
                        setMessage('');
                        setBarcode(event.target.value);
                      }}
                      placeholder="เช่น 8850123456789"
                    />
                    <button
                      type="submit"
                      disabled={!branch || !barcode.trim() || busy}
                    >
                      เช็กราคา
                    </button>
                  </div>
                </form>
              </section>
            )}
            {!camera && (
              <section className="customer-card">
                <h2>ไม่มีบาร์โค้ด / จำชื่อไม่ครบ?</h2>
                <p className="customer-search-help" id="customer-search-help">
                  พิมพ์ชื่อบางส่วนหรือชื่อที่ใกล้เคียง แล้วเลือกสินค้าจากรายการ
                </p>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void search();
                  }}
                >
                  <label htmlFor="customer-query">ชื่อสินค้า</label>
                  <div className="customer-input-row">
                    <input
                      id="customer-query"
                      value={query}
                      maxLength={80}
                      autoComplete="off"
                      aria-describedby="customer-search-help"
                      placeholder="เช่น ยาทาเล็บ ลูกอม น้ำดื่ม"
                      onChange={(event) => {
                        ++sequence.current;
                        request.current?.abort();
                        setBusy(false);
                        setSearchResult(undefined);
                        setQuote(undefined);
                        setMessage('');
                        setQuery(event.target.value);
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!branch || query.trim().length < 2 || busy}
                    >
                      ค้นหา
                    </button>
                  </div>
                </form>
              </section>
            )}
            {busy && <p role="status">กำลังค้นหาและเช็กราคา…</p>}
            {searchResult && !quote && (
              <section
                className="customer-search-results"
                aria-label="ผลการค้นหาสินค้า"
                aria-live="polite"
              >
                <h2>
                  {searchResult.items.length
                    ? 'เลือกสินค้าที่ต้องการ'
                    : 'ไม่พบสินค้าที่ใกล้เคียง'}
                </h2>
                {!searchResult.items.length && (
                  <p>ลองใช้ชื่อสั้นลง เปลี่ยนคำค้น หรือสอบถามพนักงาน</p>
                )}
                <ul>
                  {searchResult.items.map((item) => (
                    <li key={item.unitId}>
                      <button
                        className="customer-search-item"
                        onClick={() => setQuote(item)}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" loading="lazy" />
                        ) : (
                          <span
                            className="customer-product-placeholder"
                            aria-hidden="true"
                          >
                            ▧
                          </span>
                        )}
                        <span className="customer-search-item-info">
                          <strong>{item.productName}</strong>
                          <span>
                            {item.unitName}
                            {item.match === 'similar' ? ' · ชื่อใกล้เคียง' : ''}
                          </span>
                          <span className="customer-search-price">
                            ฿{baht(item.price)} / {item.unitName}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {searchResult.hasMore && (
                  <p>แสดง 20 รายการแรก กรุณาเพิ่มคำค้นให้เฉพาะเจาะจงขึ้น</p>
                )}
              </section>
            )}
            {message && (
              <p className="customer-alert" role="alert">
                {message}
              </p>
            )}
            {quote && (
              <section
                className="customer-card customer-result"
                aria-label="ผลการเช็กราคา"
                aria-live="polite"
              >
                {quote.imageUrl && (
                  <img src={quote.imageUrl} alt={quote.productName} />
                )}
                <p className="customer-eyebrow">{quote.branchName}</p>
                <h2>{quote.productName}</h2>
                <div className="customer-price">
                  ฿{baht(quote.price)} <small>/ {quote.unitName}</small>
                </div>
                {quote.price !== quote.regularPrice && (
                  <p>
                    ราคาปกติ <s>฿{baht(quote.regularPrice)}</s>
                  </p>
                )}
                <p>
                  ราคาสำหรับ 1 {quote.unitName}
                  {quote.barcode
                    ? ` · บาร์โค้ด ${quote.barcode}`
                    : ' · ไม่มีบาร์โค้ด'}
                </p>
                {quote.promotions.length > 0 && (
                  <div className="customer-promotions">
                    <h3>โปรโมชั่นและเงื่อนไข</h3>
                    {quote.promotions.map((promo, index) => (
                      <p key={index}>
                        <strong>{promo.name}</strong>
                        <br />
                        {promo.condition}
                        <br />
                        <small>
                          {promo.applied
                            ? 'รวมในราคาที่แสดงแล้ว'
                            : 'เมื่อซื้อครบตามเงื่อนไข'}
                        </small>
                      </p>
                    ))}
                  </div>
                )}
                <small>
                  ตรวจราคาเมื่อ{' '}
                  {new Date(quote.checkedAt).toLocaleTimeString('th-TH', {
                    timeZone: 'Asia/Bangkok',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  น.
                </small>
                {searchResult && (
                  <button onClick={() => setQuote(undefined)}>
                    กลับไปผลการค้นหา
                  </button>
                )}
                <button
                  onClick={() => {
                    setQuote(undefined);
                    setSearchResult(undefined);
                    setBarcode('');
                    setCamera(true);
                  }}
                >
                  สแกนชิ้นต่อไป
                </button>
              </section>
            )}
          </>
        )}
        <footer className="customer-footer">
          ใช้บัญชี LINE เพื่อตรวจสิทธิ์การเข้าใช้งานและเชื่อมตัวตนกับร้าน
          การเข้าใช้งานนี้ยังไม่ใช่การสมัครสมาชิกสะสมคะแนน
        </footer>
      </div>
    </main>
  );
}
